import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Mail, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import apiService from '../services/api';
import { InboundEmailLogEntry } from '../types';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'Created', label: 'Created' },
  { value: 'SkippedNeedsReview', label: 'Needs review' },
  { value: 'SkippedSenderNotAllowed', label: 'Sender not allowed' },
  { value: 'SkippedNotRecognized', label: 'Not recognized' },
  { value: 'SkippedDuplicate', label: 'Duplicate' },
  { value: 'Failed', label: 'Failed' },
];

const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'Created':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'SkippedNeedsReview':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 'Failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
};

const formatStatusLabel = (status: string) =>
  status.replace(/([a-z])([A-Z])/g, '$1 $2');

const InboundEmailLogPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedId = searchParams.get('id');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const pageSize = 25;

  const { data: logsResponse, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['inbound-email-logs', page, statusFilter],
    queryFn: () =>
      apiService.getInboundEmailLogs(page, pageSize, statusFilter === 'all' ? undefined : statusFilter),
  });

  const retryMutation = useMutation({
    mutationFn: (id: number) => apiService.retryInboundEmailLog(id),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Email reprocessed');
        queryClient.invalidateQueries({ queryKey: ['inbound-email-logs'] });
      } else {
        toast.error(response.message || 'Retry failed');
      }
    },
    onError: () => toast.error('Retry failed'),
  });

  const logs = logsResponse?.data?.data ?? [];
  const totalPages = logsResponse?.data?.totalPages ?? 1;
  const totalCount = logsResponse?.data?.totalCount ?? 0;

  const canRetry = (entry: InboundEmailLogEntry) =>
    ['SkippedNeedsReview', 'Failed', 'SkippedDuplicate'].includes(entry.status);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Button variant="outline" asChild>
            <Link to="/admin/email-integration" className="flex items-center space-x-2">
              <ArrowLeft className="h-4 w-4" />
              <span>Email Integration</span>
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Mail className="h-6 w-6 text-gray-900 dark:text-gray-100" />
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Inbound Email Log</h1>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {totalCount} processed email{totalCount === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      <div className="card">
        <div className="card-body flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="w-full sm:w-64">
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(1);
                if (value === 'all') {
                  searchParams.delete('status');
                } else {
                  searchParams.set('status', value);
                }
                setSearchParams(searchParams);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body overflow-x-auto">
          {isLoading ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-accent-color" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">No inbound emails match this filter.</p>
          ) : (
            <table className="min-w-full text-left">
              <thead>
                <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-2 pr-4 font-medium">Received</th>
                  <th className="pb-2 pr-4 font-medium">Subject</th>
                  <th className="pb-2 pr-4 font-medium">From</th>
                  <th className="pb-2 pr-4 font-medium">Parser</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="pb-2 pr-4 font-medium">Work order</th>
                  <th className="pb-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((entry) => (
                  <tr
                    key={entry.id}
                    className={clsx(
                      'border-b border-gray-100 dark:border-gray-800 align-top',
                      highlightedId === String(entry.id) && 'bg-accent-color/5'
                    )}
                  >
                    <td className="py-3 pr-4 text-sm whitespace-nowrap">{new Date(entry.receivedAt).toLocaleString()}</td>
                    <td className="py-3 pr-4 text-sm max-w-xs">
                      <div className="font-medium truncate" title={entry.subject}>{entry.subject || '—'}</div>
                      {entry.errorMessage && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{entry.errorMessage}</div>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-sm max-w-[180px] truncate" title={entry.fromAddress}>{entry.fromAddress || '—'}</td>
                    <td className="py-3 pr-4 text-sm">{entry.parserUsed || '—'}</td>
                    <td className="py-3 pr-4">
                      <span className={clsx('inline-flex px-2 py-1 rounded-full text-xs font-medium', statusBadgeClass(entry.status))}>
                        {formatStatusLabel(entry.status)}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-sm">
                      {entry.workOrderId ? (
                        <Link to={`/workorders/${entry.workOrderId}/edit`} className="text-accent-color hover:underline">
                          {entry.workOrderNumber || `WO #${entry.workOrderId}`}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-3">
                      {canRetry(entry) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={retryMutation.isPending}
                          onClick={() => retryMutation.mutate(entry.id)}
                        >
                          Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button type="button" variant="outline" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
            Previous
          </Button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default InboundEmailLogPage;
